import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackTestimonials = [
  {
    id: '1',
    name: 'Michael R.',
    location: 'Calgary, AB',
    rating: 5,
    text: 'DNB replaced our old furnace with a high-efficiency model. Mike was professional, clean, and explained everything. Our heating bills dropped right away.',
    service: 'Furnace Installation',
  },
  {
    id: '2',
    name: 'Sarah T.',
    location: 'Airdrie, AB',
    rating: 5,
    text: 'Had a gas line installed for our new BBQ. Quick response, fair pricing, and excellent workmanship. These guys know their stuff!',
    service: 'Gas Fitting',
  },
  {
    id: '3',
    name: 'David & Lisa M.',
    location: 'Cochrane, AB',
    rating: 5,
    text: 'Our tankless water heater installation was flawless. Mike was knowledgeable and took the time to show us how everything works.',
    service: 'Tankless Water Heater',
  },
];

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTestimonials(response.data);
        } else {
          setTestimonials(fallbackTestimonials);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials(fallbackTestimonials);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const next = () => setCurrentIndex((p) => (p + 1) % testimonials.length);
  const prev = () => setCurrentIndex((p) => (p - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  if (loading || testimonials.length === 0) {
    return (
      <section className="py-24 section-tint">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-10 h-10 border-2 border-slate-300 border-t-[#0284C7] rounded-full animate-spin mx-auto" />
        </div>
      </section>
    );
  }

  const current = testimonials[currentIndex];

  return (
    <section data-testid="testimonials-section" className="py-24 section-tint relative overflow-hidden">
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="eyebrow mb-5">Customer Stories</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#0F172A] mt-5 mb-4 leading-tight">
            What our Calgary <br className="hidden sm:block" />
            <span className="text-[#0284C7]">customers say.</span>
          </h2>
        </motion.div>

        {/* Testimonial Card */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              data-testid={`testimonial-${currentIndex}`}
              className="relative bg-white rounded-lg border border-slate-200 shadow-xl shadow-slate-200/50 p-10 lg:p-14 text-center"
            >
              <Quote className="absolute top-6 left-6 w-12 h-12 text-slate-300" strokeWidth={1} fill="currentColor" />

              <div className="flex justify-center gap-1 mb-6 relative">
                {[...Array(current.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400" fill="currentColor" strokeWidth={0} />
                ))}
              </div>

              <blockquote className="font-heading text-xl lg:text-2xl text-[#0F172A] mb-8 leading-snug relative">
                &ldquo;{current.text}&rdquo;
              </blockquote>

              <div className="relative">
                <p className="font-heading font-extrabold text-[#0F172A] text-lg">{current.name}</p>
                <p className="text-slate-500 text-sm">{current.location}</p>
                {current.service && (
                  <p className="text-[#0284C7] text-xs uppercase tracking-widest font-bold mt-2">
                    {current.service}
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={prev}
              data-testid="testimonial-prev-btn"
              className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 rounded-full hover:border-[#0284C7] hover:text-[#0284C7] transition-all shadow-sm"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-[#0284C7] w-7' : 'bg-sky-200 w-2 hover:bg-sky-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              data-testid="testimonial-next-btn"
              className="w-11 h-11 flex items-center justify-center bg-white border border-slate-200 rounded-full hover:border-[#0284C7] hover:text-[#0284C7] transition-all shadow-sm"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
